import React from 'react';
import PropTypes from 'prop-types';
import { withScope } from '@sentry/browser';
import captureException from '%app/js/common/services/captureException';
import isPageLoadFailure from '%app/js/common/services/isPageLoadFailure';

function reloadUnlessOffline() {
    if (window.navigator.onLine !== false) {
        window.location.reload();
    }
}

class PageLoadBoundary extends React.Component {
    static getDerivedStateFromError(error) {
        return { error };
    }

    constructor(props) {
        super(props);

        this.state = {
            error: null,
        };
        this.caughtErrors = new Set();
        this.retry = this.retry.bind(this);
    }

    componentDidMount() {
        window.addEventListener('online', this.retry);
    }

    componentDidUpdate(previousProps) {
        const { resetKey } = this.props;

        if (previousProps.resetKey !== resetKey) {
            this.retry();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('online', this.retry);
    }

    componentDidCatch(error, errorInfo) {
        if (this.caughtErrors.has(error)) {
            reloadUnlessOffline();
        } else {
            this.caughtErrors.add(error);
            withScope((scope) => {
                Object.keys(errorInfo).forEach((key) => {
                    scope.setExtra(key, errorInfo[key]);
                });
                captureException(error);
            });
        }
    }

    retry() {
        this.setState(({ error }) => (error === null ? null : { error: null }));
    }

    render() {
        const { children, renderFallback } = this.props;
        const { error } = this.state;

        if (error === null) {
            return children;
        }

        if (!isPageLoadFailure(error)) {
            throw error;
        }

        return renderFallback({ onRetry: this.retry });
    }
}

PageLoadBoundary.propTypes = {
    children: PropTypes.node.isRequired,
    renderFallback: PropTypes.func.isRequired,
    resetKey: PropTypes.string,
};

PageLoadBoundary.defaultProps = {
    resetKey: null,
};

export default PageLoadBoundary;
